import {
    resolveConnectorEndpoint,
    resolveLayoutAnchorWorldPosition,
    type AnchorResolutionContext,
} from '@toolproof-core/visualization/_pure/entity-anchor-resolution';
import type { ItemCollection } from '@toolproof-core/visualization/_contract/collection';
import type { Vector3Tuple } from '@toolproof-core/visualization/_contract/geometry';
import type { ConnectorItem, ConnectorItemParametricState } from '@toolproof-core/visualization/_contract/item';
import type {
    InstantFramePatch,
    InterpolatedFramePatch,
    SpaceDerivationFrame,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import {
    parseMacroStepConnectorCollectionKey,
    parseResourceTypeRadialConnectorItemKey,
    parseWorkflowStateEntryItemKey,
} from '@/workflow/_pure/keyCodec';

export function isStructuralGuide(collection: ItemCollection, item: ConnectorItem, state: ConnectorItemParametricState | undefined): boolean {
    return parseResourceTypeRadialConnectorItemKey(item.itemKey) !== null
        || (parseMacroStepConnectorCollectionKey(collection.itemCollectionKey) !== null
            && (state?.path?.pathKind ?? 'straight') === 'straight');
}

function anchorContext(frame: SpaceDerivationFrame): AnchorResolutionContext {
    return {
        entityConstellations: frame.constellations.filter((entry) => entry.constellationKind === 'entity'),
        entityItemsByCollectionKey: new Map(frame.itemCollections.flatMap((collection) => (
            collection.itemCollectionKind === 'entity' ? [[collection.itemCollectionKey, collection.items] as const] : []
        ))),
        itemParametricStateByKey: frame.itemParametricState?.entityItemByKey ?? {},
        entityConstellationParametricStateByKey: frame.constellationParametricState?.entityConstellationByKey ?? {},
    };
}

function guideItems(frame: SpaceDerivationFrame): Map<ConnectorItem['itemKey'], ConnectorItem> {
    return new Map(frame.itemCollections.flatMap((collection) => (
        collection.itemCollectionKind === 'connector'
            ? collection.items.filter((item) => isStructuralGuide(collection, item,
                frame.itemParametricState?.connectorItemByKey?.[item.itemKey]))
                .map((item) => [item.itemKey, item] as const)
            : []
    )));
}

function resolvePositions(item: ConnectorItem, context: AnchorResolutionContext) {
    const from = resolveConnectorEndpoint({ endpoint: item.endpoints.from, context })?.position;
    const to = resolveConnectorEndpoint({ endpoint: item.endpoints.to, context })?.position;
    if (!from || !to) throw new Error(`Structural guide endpoints could not resolve: ${item.itemKey}`);
    return { from, to };
}

function ringLayout(frame: SpaceDerivationFrame, constellationKey: string | undefined) {
    const layout = frame.constellationParametricState?.entityConstellationByKey?.[
        constellationKey as keyof NonNullable<NonNullable<typeof frame.constellationParametricState>['entityConstellationByKey']>
    ]?.layout;
    return layout?.layoutKind === 'concentric-ring' ? layout : undefined;
}

function ringRadii(frame: SpaceDerivationFrame, collectionKey: string | undefined, constellationKey: string | undefined) {
    const layout = ringLayout(frame, constellationKey);
    const items = frame.itemCollections.find((collection) => collection.itemCollectionKey === collectionKey)?.items ?? [];
    return new Map(items.flatMap((item, index) => (
        layout
            ? [[item.itemKey, layout.ringRadii?.[index] ?? layout.startRadius + index * layout.ringGap] as const]
            : []
    )));
}

function rimRadius(frame: SpaceDerivationFrame, itemKey: string | undefined): number | undefined {
    const state = frame.itemParametricState?.entityItemByKey?.[
        itemKey as keyof NonNullable<NonNullable<typeof frame.itemParametricState>['entityItemByKey']>
    ];
    return state?.shape?.shapeKind === 'cylinder-wall'
        ? (state.shape.innerRadius + state.shape.outerRadius) / 2
        : undefined;
}

/**
 * Keep the source radial partition visible during a space shift. Only its geometry moves;
 * the destination partition takes over atomically once the shift has finished.
 */
export function deriveStructuralGuideMotion(args: {
    base: SpaceDerivationFrame;
    target: SpaceDerivationFrame;
    toolStepCollectionKey: string | undefined;
    toolStepConstellationKey: string | undefined;
    rimItemKey: string | undefined;
    expanding: boolean;
}): {
    startPatch: InstantFramePatch;
    motionPatch: InterpolatedFramePatch;
    settlePatch: InstantFramePatch;
    holdGuides: (collections: ItemCollection[]) => ItemCollection[];
} {
    const sourceGuides = guideItems(args.base);
    const targetGuides = guideItems(args.target);
    const sourceContext = anchorContext(args.base);
    const targetContext = anchorContext(args.target);
    const resolveCenter = (frame: SpaceDerivationFrame, context: AnchorResolutionContext): Vector3Tuple => {
        const layout = ringLayout(frame, args.toolStepConstellationKey);
        const center = layout && resolveLayoutAnchorWorldPosition({ anchor: layout.anchor, context });
        if (!center) throw new Error('Structural guide motion requires a resolvable ToolStep ring center.');
        return center;
    };
    const sourceCenter = resolveCenter(args.base, sourceContext);
    const targetCenter = resolveCenter(args.target, targetContext);
    const radiusAt = (position: Vector3Tuple, center: Vector3Tuple) => Math.hypot(position[0] - center[0], position[2] - center[2]);
    const sourceRadii = ringRadii(args.base, args.toolStepCollectionKey, args.toolStepConstellationKey);
    const targetRadii = ringRadii(args.target, args.toolStepCollectionKey, args.toolStepConstellationKey);
    const sourceRim = rimRadius(args.base, args.rimItemKey);
    const targetRim = rimRadius(args.target, args.rimItemKey);
    const radialStarts = [...sourceGuides.values()].filter((item) => parseResourceTypeRadialConnectorItemKey(item.itemKey));
    const innerRadius = radialStarts.length === 0 ? 0 : Math.min(...radialStarts.flatMap((item) => {
        const positions = resolvePositions(item, sourceContext);
        return [radiusAt(positions.from, sourceCenter), radiusAt(positions.to, sourceCenter)];
    }));
    const knots: Array<[number, number]> = [[innerRadius, innerRadius]];
    for (const [key, radius] of sourceRadii) {
        const targetRadius = targetRadii.get(key);
        if (targetRadius !== undefined) knots.push([radius, targetRadius]);
    }
    if (sourceRim !== undefined) {
        knots.push([sourceRim, targetRim ?? innerRadius]);
    }
    knots.sort((a, b) => a[0] - b[0]);
    const mapPosition = (position: Vector3Tuple): Vector3Tuple => {
        const radius = radiusAt(position, sourceCenter);
        if (radius === 0) return [targetCenter[0], position[1] + targetCenter[1] - sourceCenter[1], targetCenter[2]];
        const upperIndex = knots.findIndex(([source]) => source >= radius - 1e-8);
        const upper = knots[upperIndex < 0 ? knots.length - 1 : upperIndex];
        const lower = knots[Math.max(0, (upperIndex < 0 ? knots.length - 1 : upperIndex) - 1)];
        const fraction = upper[0] === lower[0] ? 1 : Math.max(0, Math.min(1, (radius - lower[0]) / (upper[0] - lower[0])));
        const mapped = lower[1] + fraction * (upper[1] - lower[1]);
        return [
            targetCenter[0] + (position[0] - sourceCenter[0]) * mapped / radius,
            position[1] + targetCenter[1] - sourceCenter[1],
            targetCenter[2] + (position[2] - sourceCenter[2]) * mapped / radius,
        ];
    };
    const start: NonNullable<NonNullable<InstantFramePatch['itemParametricState']>['connectorItemByKey']> = {};
    const motion: NonNullable<NonNullable<InterpolatedFramePatch['itemParametricState']>['connectorItemByKey']> = {};
    const settle: NonNullable<NonNullable<InstantFramePatch['itemParametricState']>['connectorItemByKey']> = {};
    const heldGuides = new Map(sourceGuides);
    for (const [key, item] of sourceGuides) {
        const positions = resolvePositions(item, sourceContext);
        const radial = parseResourceTypeRadialConnectorItemKey(key);
        const targetItem = targetGuides.get(key);
        const end = !radial && targetItem
            ? resolvePositions(targetItem, targetContext)
            : { from: mapPosition(positions.from), to: mapPosition(positions.to) };
        if (!radial && !targetItem) end.to = end.from;
        start[key] = { endpointPositions: positions };
        motion[key] = { endpointPositions: end };
        if (radial?.direction === 'output') {
            const panels = args.base.itemCollections.flatMap((collection) => (
                collection.itemCollectionKind === 'entity' ? collection.items.filter((panel) => {
                    const parsed = parseWorkflowStateEntryItemKey(panel.itemKey);
                    return parsed?.stepKey === radial.toolStepKey && parsed.direction === 'output'
                        && `item:entity:${parsed.resourceTypeHandle}` === radial.resourceTypeItemKey;
                }) : []
            )).map((panel) => {
                const anchor = { anchorKind: 'item' as const, anchorItemKey: panel.itemKey };
                const position = resolveLayoutAnchorWorldPosition({ anchor, context: sourceContext });
                if (!position) throw new Error(`Structural guide output panel could not resolve: ${panel.itemKey}`);
                const targetPosition = resolveLayoutAnchorWorldPosition({ anchor, context: targetContext });
                return { position, targetPosition: targetPosition ?? mapPosition(position) };
            }).sort((a, b) => radiusAt(a.position, sourceCenter) - radiusAt(b.position, sourceCenter));
            const lastPanel = panels.at(-1);
            if (lastPanel) {
                start[key] = { ...start[key], activationFrontPosition: lastPanel.position };
                motion[key] = { ...motion[key], activationFrontPosition: lastPanel.targetPosition };
            }
        }
    }
    if (args.expanding) {
        for (const [key, item] of targetGuides) {
            if (sourceGuides.has(key) || parseResourceTypeRadialConnectorItemKey(key)) continue;
            const end = resolvePositions(item, targetContext);
            const radius = radiusAt(end.from, targetCenter);
            const fromEndpoint = item.endpoints.from;
            const sourceOwnerRadius = fromEndpoint.endpointKind === 'parametric'
                ? sourceRadii.get(fromEndpoint.ringItemKey) : undefined;
            const precedingRadius = [...targetRadii].filter(([key, targetRadius]) =>
                targetRadius <= radius && sourceRadii.has(key)).at(-1);
            const ownerRadius = (sourceOwnerRadius
                ?? (precedingRadius ? sourceRadii.get(precedingRadius[0]) : undefined)
                ?? innerRadius) + (fromEndpoint.endpointKind === 'parametric' ? fromEndpoint.radialOffset ?? 0 : 0);
            const collapsed: Vector3Tuple = radius === 0 ? end.from
                : [
                    sourceCenter[0] + (end.from[0] - targetCenter[0]) * ownerRadius / radius,
                    end.from[1] + sourceCenter[1] - targetCenter[1],
                    sourceCenter[2] + (end.from[2] - targetCenter[2]) * ownerRadius / radius,
                ];
            heldGuides.set(key, item);
            start[key] = { ...args.target.itemParametricState?.connectorItemByKey?.[key],
                endpointPositions: { from: collapsed, to: collapsed } };
            motion[key] = { endpointPositions: end };
        }
    }
    for (const [key] of targetGuides) {
        settle[key] = {
            ...args.target.itemParametricState?.connectorItemByKey?.[key],
            endpointPositions: null,
            activationFrontPosition: null,
        };
    }
    const holdGuides = (collections: ItemCollection[]): ItemCollection[] => {
        const result = collections.map((collection) => {
            if (collection.itemCollectionKind !== 'connector') return collection;
            const items = collection.items.filter((item) => !sourceGuides.has(item.itemKey) && !targetGuides.has(item.itemKey));
            const guideKeys = new Set([
                ...args.base.itemCollections.find((entry) => entry.itemCollectionKey === collection.itemCollectionKey)?.items.map((item) => item.itemKey) ?? [],
                ...args.target.itemCollections.find((entry) => entry.itemCollectionKey === collection.itemCollectionKey)?.items.map((item) => item.itemKey) ?? [],
            ]);
            for (const [key, item] of heldGuides) if (guideKeys.has(key)) items.push(item);
            return { ...collection, items };
        });
        for (const collection of [...args.base.itemCollections, ...args.target.itemCollections]) {
            if (collection.itemCollectionKind !== 'connector' || result.some((entry) => entry.itemCollectionKey === collection.itemCollectionKey)) continue;
            const items = collection.items.filter((item) => heldGuides.has(item.itemKey));
            if (items.length > 0) result.push({ ...collection, items });
        }
        return result;
    };
    return {
        startPatch: {
            itemCollections: holdGuides(args.base.itemCollections),
            constellations: [...args.base.constellations, ...args.target.constellations.filter((entry) =>
                entry.constellationKind === 'connector'
                && !args.base.constellations.some((source) => source.constellationKey === entry.constellationKey))],
            itemParametricState: { connectorItemByKey: start },
        },
        motionPatch: { itemParametricState: { connectorItemByKey: motion } },
        settlePatch: { itemCollections: args.target.itemCollections, itemParametricState: { connectorItemByKey: settle } },
        holdGuides,
    };
}
